import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { trackBuyingJourney } from '@/services/customerJourneyService'
import {
  JOURNEY_FUNNEL_STAGES,
  JOURNEY_FUNNEL_EVENTS
} from '@/constants/buyingJourney'
import {
  Sparkles,
  Award,
  Shield,
  ArrowRight,
  Gamepad2,
  CheckCircle2
} from 'lucide-react'
import './LpHeroSection.css'

const HERO_IMAGE_URL = '/assets/landing/nlpHero.webp'

const LpHeroSection = () => {
  const navigate = useNavigate()
  return (
    <section className='lp-hero-section'>
      <motion.div
        className='lp-hero-section__inner'
        initial={{ opacity: 0, x: -60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className='lp-hero-section__content-cta-wrapper'>
          <div className='lp-hero-section__content'>
            <motion.h1
              className='lp-hero-section__headline'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Children Are <br />
              <span className='lp-hero-section__headline-highlight'>
                One Click <br className='mobile-br' /> Away <br />
              </span>{' '}
              From Danger
            </motion.h1>

            <span className='lp-hero-section__ages'>Ages 8-12</span>
            <motion.p
              className='lp-hero-section__description'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Digipalz teaches kids how to spot online dangers, protect their
              privacy, and navigate the web safely.
            </motion.p>
            <motion.ul
              className='lp-hero-section__features'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              {[
                {
                  icon: Shield,
                  size: 14,
                  text: '12 original story-based online safety lessons'
                },
                {
                  icon: Sparkles,
                  size: 16,
                  text: 'Interactive missions with puzzle solving'
                },
                {
                  icon: Award,
                  size: 16,
                  text: 'Discussion prompts for parents & teachers'
                }
              ].map((item, index) => {
                const Icon = item.icon
                return (
                  <motion.li
                    key={index}
                    className='lp-hero-section__feature'
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                  >
                    <span className='lp-hero-section__feature-icon'>
                      <Icon size={item.size} strokeWidth={2.5} />
                    </span>
                    <span>{item.text}</span>
                  </motion.li>
                )
              })}
            </motion.ul>
          </div>
          <motion.div
            className='lp-hero-section__cta-wrap'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <motion.button
              type='button'
              className='lp-hero-section__cta'
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                trackBuyingJourney({
                  stage: JOURNEY_FUNNEL_STAGES.A_LANDING,
                  eventName: JOURNEY_FUNNEL_EVENTS.CONTENT_ENGAGEMENT,
                  cta: 'start_free_trial'
                })
                navigate('/register')
              }}
            >
              <Sparkles size={20} strokeWidth={2.5} aria-hidden='true' />
              <span>START FREE TRIAL NOW</span>
              <ArrowRight size={20} strokeWidth={2.5} aria-hidden='true' />
            </motion.button>
            <p className='lp-hero-section__disclaimer'>
              <CheckCircle2 size={20} aria-hidden='true' />
              <span>7-day free trial • Cancel anytime</span>
            </p>
          </motion.div>
        </div>
        <motion.div
          className='lp-hero-section__visual'
          initial={{ opacity: 0, scale: 0.9, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className='lp-hero-section__visual-frame'>
            <div className='lp-hero-section__visual-inner'>
              <img
                className='lp-hero-section__visual-image'
                src={HERO_IMAGE_URL}
                alt='Kids learning online safety with Digipalz'
                width={300}
                height={400}
                fetchPriority='high'
                loading='eager'
                decoding='async'
              />
            </div>
            <div className='lp-hero-section__overlay lp-hero-section__overlay--missions'>
              <span className='lp-hero-section__overlay-number'>12</span>
              <span className='lp-hero-section__overlay-label'>Missions</span>
            </div>
            <div className='lp-hero-section__overlay lp-hero-section__overlay--game'>
              <span className='lp-hero-section__overlay-game-icon-wrap'>
                <Gamepad2 size={28} strokeWidth={2} aria-hidden='true' />
              </span>
              <span className='lp-hero-section__overlay-game-text'>
                <span className='lp-hero-section__overlay-game-title'>
                  Game-Based
                </span>
                <span className='lp-hero-section__overlay-game-subtitle'>
                  Learning
                </span>
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}

export default LpHeroSection
